import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ApiError } from 'app/core/errors/api-error';
import { PreferencesApi } from 'app/domains/aya/apis/preferences.api';
import {
  EnumCommunicationEmojis,
  EnumCommunicationPersonality,
  EnumCommunicationResponseStyle
} from 'app/domains/aya/enums/communication-preference.enum';
import {
  hasKnownCommunicationChoices,
  ICommunicationPreferences,
  ICommunicationPreferencesRequest
} from 'app/domains/aya/interfaces/communication-preferences.interface';
import { ToastService } from 'app/domains/shared/components/toast/services/toast.service';

const MAX_CUSTOM_PERSONALITY_LENGTH = 1000;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

const SAVE_ERROR_MESSAGES: { [code: string]: string } = {
  validation_error: 'Revise as opções e o texto personalizado antes de salvar.',
  rate_limit_exceeded:
    'Muitas tentativas em pouco tempo. Aguarde antes de salvar novamente.',
  preferences_unavailable:
    'O serviço de preferências está indisponível no momento. Suas alterações foram mantidas.'
};

const EMOJI_OPTIONS = [
  { value: EnumCommunicationEmojis.None, label: 'Não usar' },
  { value: EnumCommunicationEmojis.Moderate, label: 'Com moderação' },
  { value: EnumCommunicationEmojis.Frequent, label: 'Usar mais' }
];

const RESPONSE_STYLE_OPTIONS = [
  { value: EnumCommunicationResponseStyle.Concise, label: 'Concisa' },
  { value: EnumCommunicationResponseStyle.Balanced, label: 'Equilibrada' },
  { value: EnumCommunicationResponseStyle.Explanatory, label: 'Explicativa' }
];

const PERSONALITY_OPTIONS = [
  { value: EnumCommunicationPersonality.Professional, label: 'Profissional' },
  { value: EnumCommunicationPersonality.Friendly, label: 'Amigável' },
  { value: EnumCommunicationPersonality.Casual, label: 'Descontraída' },
  { value: EnumCommunicationPersonality.Custom, label: 'Personalizada' }
];

const PREVIEW_OPENING: { [key in EnumCommunicationPersonality]: string } = {
  [EnumCommunicationPersonality.Professional]: 'Analisei as vendas do período.',
  [EnumCommunicationPersonality.Friendly]:
    'Que bom ajudar! Analisei as vendas do período.',
  [EnumCommunicationPersonality.Casual]:
    'Bora lá! Dei uma olhada nas vendas do período.',
  [EnumCommunicationPersonality.Custom]: 'Analisei as vendas do período.'
};

const PREVIEW_DETAIL: { [key in EnumCommunicationResponseStyle]: string } = {
  [EnumCommunicationResponseStyle.Concise]: 'O resultado melhorou.',
  [EnumCommunicationResponseStyle.Balanced]:
    'O resultado melhorou, e vale priorizar os clientes em queda.',
  [EnumCommunicationResponseStyle.Explanatory]:
    'O resultado melhorou. Isso indica uma boa resposta da carteira, mas alguns clientes seguem em queda: vale priorizá-los e acompanhar a evolução no próximo período.'
};

const PREVIEW_EMOJI: { [key in EnumCommunicationEmojis]: string } = {
  [EnumCommunicationEmojis.None]: '',
  [EnumCommunicationEmojis.Moderate]: ' 🙂',
  [EnumCommunicationEmojis.Frequent]: ' 📊📈✅'
};

@Component({
  selector: 'sgt-general-section',
  templateUrl: './general-section.component.html',
  styleUrls: ['./general-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeneralSectionComponent implements OnInit, OnDestroy {
  readonly emojiOptions = EMOJI_OPTIONS;
  readonly responseStyleOptions = RESPONSE_STYLE_OPTIONS;
  readonly personalityOptions = PERSONALITY_OPTIONS;
  readonly maxLength = MAX_CUSTOM_PERSONALITY_LENGTH;

  readonly skeletonGroups = [
    EMOJI_OPTIONS,
    RESPONSE_STYLE_OPTIONS,
    PERSONALITY_OPTIONS
  ];

  readonly form = new FormGroup({
    emojis: new FormControl(null),
    responseStyle: new FormControl(null),
    personality: new FormControl(null),
    customPersonality: new FormControl('')
  });

  isLoading = false;
  isSaving = false;
  hasLoadError = false;
  isForbidden = false;

  private destroy$ = new Subject<void>();

  constructor(
    private preferencesApi: PreferencesApi,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  get isCustom(): boolean {
    return (
      this.form.get('personality')?.value ===
      EnumCommunicationPersonality.Custom
    );
  }

  get customLength(): number {
    return Array.from(this.trimmedCustomText).length;
  }

  get customError(): string | null {
    if (this.customLength > MAX_CUSTOM_PERSONALITY_LENGTH) {
      return `Use até ${MAX_CUSTOM_PERSONALITY_LENGTH} caracteres.`;
    }
    if (this.isCustom && !this.customLength) {
      return 'Descreva como a AyA deve se comunicar ou escolha outra personalidade.';
    }
    return null;
  }

  get canSave(): boolean {
    return this.form.dirty && !this.isSaving && !this.customError;
  }

  get previewText(): string {
    const { emojis, personality, responseStyle }: ICommunicationPreferencesRequest =
      this.form.getRawValue();
    return `${PREVIEW_OPENING[personality]} ${PREVIEW_DETAIL[responseStyle]}${PREVIEW_EMOJI[emojis]}`;
  }

  ngOnInit(): void {
    this.form.controls.personality.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((personality) => {
        if (personality !== EnumCommunicationPersonality.Custom) {
          this.form.controls.customPersonality.setValue('');
        }
      });

    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.isLoading = true;
    this.hasLoadError = false;
    this.isForbidden = false;

    this.preferencesApi
      .get()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preferences) => {
          this.isLoading = false;
          this.applySaved(preferences);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoading = false;
          this.blockEditing(error);
          this.cdr.markForCheck();
        }
      });
  }

  save(): void {
    if (!this.canSave) {
      return;
    }

    const request: ICommunicationPreferencesRequest = {
      ...this.form.getRawValue(),
      customPersonality: this.trimmedCustomText || null
    };
    this.isSaving = true;
    this.form.disable({ emitEvent: false });

    this.preferencesApi
      .update(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preferences) => {
          this.isSaving = false;
          this.applySaved(preferences);
          if (!this.hasLoadError) {
            this.toastService.success('Preferências salvas.');
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSaving = false;
          this.form.enable({ emitEvent: false });
          this.handleSaveError(error);
          this.cdr.markForCheck();
        }
      });
  }

  private get trimmedCustomText(): string {
    return (this.form.get('customPersonality')?.value ?? '').trim();
  }

  private applySaved(preferences: ICommunicationPreferences): void {
    if (!hasKnownCommunicationChoices(preferences)) {
      this.hasLoadError = true;
      return;
    }

    this.form.enable({ emitEvent: false });
    this.form.reset(
      {
        emojis: preferences.emojis,
        responseStyle: preferences.responseStyle,
        personality: preferences.personality,
        customPersonality: preferences.customPersonality ?? ''
      },
      { emitEvent: false }
    );
  }

  private handleSaveError(error: unknown): void {
    const status = (error as { status?: number })?.status;
    if (status === HTTP_UNAUTHORIZED) {
      return;
    }
    if (status === HTTP_FORBIDDEN) {
      this.blockEditing(error);
      return;
    }
    this.toastService.error(
      SAVE_ERROR_MESSAGES[this.errorCode(error)] ??
        'Não foi possível salvar. Suas alterações foram mantidas; tente novamente.'
    );
  }

  private blockEditing(error: unknown): void {
    this.hasLoadError = true;
    this.isForbidden =
      (error as { status?: number })?.status === HTTP_FORBIDDEN;
  }

  private errorCode(error: unknown): string {
    const body = (error as ApiError)?.body as
      | { error?: { code?: string } }
      | undefined;
    return body?.error?.code ?? '';
  }
}
